'use client';

import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import type { TomVendingSiteVisitHistoryMachine } from '@/hooks/useTomVendingSiteVisitHistory';

export function formatVisitDateLabel(ymd: string): string {
  const parts = ymd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatAppliedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const TomsVendingSiteVisitDetailContent: React.FC<{
  loading: boolean;
  machines: TomVendingSiteVisitHistoryMachine[];
}> = ({ loading, machines }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (machines.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        No machine snapshots for this visit.
      </Typography>
    );
  }

  return (
    <>
      {machines.map((m) => {
        const title =
          (m.assetId ?? '').trim() !== ''
            ? `Machine ${m.assetId}`
            : `Machine #${m.vendingMachineId}`;
        return (
          <Box key={m.vendingMachineId} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Saved {formatAppliedAt(m.appliedAt)}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>UPC</TableCell>
                    <TableCell align="right">Par</TableCell>
                    <TableCell align="right">Fill</TableCell>
                    <TableCell align="right">Rej.</TableCell>
                    <TableCell align="right">Rec.</TableCell>
                    <TableCell align="right">Final</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {m.lines.map((ln) => (
                    <TableRow key={ln.inventoryItemId}>
                      <TableCell>{ln.itemName ?? '—'}</TableCell>
                      <TableCell>{ln.upc}</TableCell>
                      <TableCell align="right">{ln.parQty}</TableCell>
                      <TableCell align="right">{ln.fillQty}</TableCell>
                      <TableCell align="right">{ln.rejectQty}</TableCell>
                      <TableCell align="right">{ln.recycleQty}</TableCell>
                      <TableCell align="right">
                        <Chip label={ln.endingQty} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </>
  );
};

export default TomsVendingSiteVisitDetailContent;
